import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import { getAllItemsOfType } from "./cartSelectors";
import { getCheckout, State as CheckoutState } from "./checkout";
import { getExistingSubscriptionsNotInCart } from "./combinedSelectors";
import { getLegalOwnerFullName, getLegalOwnerPhone } from "./customerInfoSelectors";
import { getSelectedPhoneNumber as selectedPhoneNumberSelector } from "./phoneNumberSelectors";
import {
    getProductUserFullName,
    getSubscriptionName,
    hasProductUserData as hasProductUserDataSelector,
    hasSubscriptions as hasSubscriptionsSelector,
} from "./subscriptionsSelectors";
import { CART_ITEM_TYPE, MaybeEmpty } from "../../../../../constants";
import {
    AccountExistingLegalOwner,
    AccountNewLegalOwner,
    AgreementToKeep,
    ExistingSubscription,
    TransferState,
} from "../../../../../models";
import {
    AccountCurrent,
    AccountExternal,
    AccountMember,
    AccountState,
    AccountSubscription,
} from "../../../../../models/state";

export type State = CheckoutState;
export const getAccount = (state: State): MaybeEmpty<AccountState> => getCheckout(state).account || {};
export const getAccountType = (state: State) => getAccount(state).type;
export const getAccountId = (state: State) => getAccount(state).accountId;
export const getAccountsWithSubscriptions = (state: State) => getAccount(state).accountIdsWithTypes || [];
export const getExistingSubscriptionsInGroup = (state: State): AccountSubscription[] => {
    const selectedBan = getAccountId(state);
    const accountIdsWithTypes = getAccountsWithSubscriptions(state);
    const currentAccount = accountIdsWithTypes.find((account) => account.accountId === selectedBan);
    return _get(currentAccount, "subscriptions") || [];
};

//////////////////
//// External ////
//////////////////
export const getExternal = (state: State): MaybeEmpty<AccountExternal> => getAccount(state).external || {};
export const hasExternalLegalOwner = (state: State) => !_isEmpty(getExternal(state));
export const getExternalExistingNumbersInOrder = (state: State) => Object.keys(getExternal(state).existingOrders || {});
export const getInitiatedBy = (state: State): string => getExternal(state).initiatedBy || "";
export const getExternalShowB2BFamilyOffer = (state: State): boolean => !!getExternal(state).showB2BFamilyOffer;

//////////////////
//// Members ////
//////////////////
export const getSubscriptionGroupMembers = (state: State) => {
    const subscriptionsInCart = getAllItemsOfType(state, CART_ITEM_TYPE.SUBSCRIPTION);
    const hasSubscriptions = hasSubscriptionsSelector(state);
    const isB2BFamilyOffer = getExternalShowB2BFamilyOffer(state);
    const members: AccountMember[] = [];

    if (!hasSubscriptions) {
        return members;
    }

    // Add on legal always
    if (isB2BFamilyOffer) {
        members.push({
            name: "Bedriftsabonnement",
            isLegal: true,
            isExisting: hasExternalLegalOwner(state),
            isB2BFamilyOffer: true,
        });
    } else {
        members.push({
            name: getLegalOwnerFullName(state),
            number: getLegalOwnerPhone(state),
            isLegal: true,
            isExisting: hasExternalLegalOwner(state),
        });
    }

    // Cart Subscriptions
    subscriptionsInCart.forEach((cartSubscription) => {
        const id = cartSubscription.bundleId;
        const subscriptionName = getSubscriptionName(state, id);
        const hasProductUserData = hasProductUserDataSelector(state, id);
        const name = getProductUserFullName(state, id);
        const number = selectedPhoneNumberSelector(state, id);

        const member = {
            id,
            name,
            number,
            subscriptionName,
            isLegal: false,
            isExisting: false,
        };

        if (hasProductUserData) {
            members.push(member);
        }
    });

    // Get existing subscriptions on BAN
    const existingMembers: AccountMember[] = getExistingSubscriptionsNotInCart(state);

    // Get existing subscriptions being in order under processing
    const existingNumbersInOrder = getExternalExistingNumbersInOrder(state);
    const existingSubscriptions = getExistingSubscriptionsInGroup(state);
    existingSubscriptions.forEach((sub) => {
        if (existingNumbersInOrder.includes(sub.number)) {
            // Here we have an existing subscription in order under processing
            existingMembers.push({
                name: sub.name,
                number: sub.number,
                isInProcess: true,
                isExisting: true,
            });
        }
    });

    // Append existing subscriptions after legal
    members.splice(1, 0, ...existingMembers);

    return members;
};

export const getCurrentSubscriptionMember = (state: State): MaybeEmpty<AccountCurrent> =>
    getAccount(state).current || {};
export const getCurrentSubscriptionMemberId = (state: State) => getCurrentSubscriptionMember(state).id;
export const getCurrentSubscriptionMemberMessages = (state: State) =>
    getCurrentSubscriptionMember(state).messages || [];

//////////////////
//// Transfer ////
//////////////////
export const getRelatedAccounts = (state: State) => getAccount(state).relatedAccounts || [];
export const getSelectedAccount = (state: State) => getAccount(state).selectedAccount || "";
export const getTransfer = (state: State): MaybeEmpty<TransferState> => getAccount(state).transfer || {};
export const getTransferBreakageFee = (state: State) => getTransfer(state).breakageFeeAmount || 0;
export const getTransferExistingLegalOwner = (state: State) => getTransfer(state).existingLegalOwner || {};

export const getTransferExistingLegalOwnerField = <
    T extends keyof AccountExistingLegalOwner,
    F extends keyof AccountExistingLegalOwner[T],
>(
    state: State,
    type: T,
    field: F,
) => _get(getTransferExistingLegalOwner(state), [type, field], "");

export const getTransferExistingIndividualLegalOwnerFullName = (state: State): string | undefined => {
    const existingLegalOwner = getTransferExistingLegalOwner(state);
    if (!existingLegalOwner || !existingLegalOwner.individual) {
        return undefined;
    }

    const firstName = existingLegalOwner.individual.firstName || "";
    const lastName = existingLegalOwner.individual.lastName || "";
    if (!firstName && !lastName) {
        return undefined;
    }

    return `${firstName} ${lastName}`.trim();
};
export const getTransferExistingOrganizationName = (state: State): string | undefined => {
    const existingLegalOwner = getTransferExistingLegalOwner(state);
    if (!existingLegalOwner || !existingLegalOwner.organization) {
        return undefined;
    }
    if (!existingLegalOwner.organization.name) {
        return undefined;
    }

    return existingLegalOwner.organization.name.trim();
};
export const getTransferExistingOrganizationNumber = (state: State): string | undefined => {
    const existingLegalOwner = getTransferExistingLegalOwner(state);
    if (!existingLegalOwner || !existingLegalOwner.organization) {
        return undefined;
    }
    if (!existingLegalOwner.organization.organizationNumber) {
        return undefined;
    }

    return existingLegalOwner.organization.organizationNumber.trim();
};
export const getTransferExistingOrganizationAdminPhoneNumbers = (state: State): string[] | undefined => {
    const existingLegalOwner = getTransferExistingLegalOwner(state);
    if (!existingLegalOwner || !existingLegalOwner.organization) {
        return undefined;
    }
    if (!existingLegalOwner.organization.organizationNumber && !existingLegalOwner.organization.orgNr) {
        return undefined;
    }

    return existingLegalOwner.organization.adminPhoneNumbers;
};
export const getTransferNewLegalOwner = (state: State): MaybeEmpty<AccountNewLegalOwner> =>
    getTransfer(state).newLegalOwner || {};
export const getTransferNewLegalOwnerByType = <T extends keyof AccountNewLegalOwner>(state: State, type: T) =>
    getTransferNewLegalOwner(state)[type] || null;
export const getTransferNewLegalOwnerField = <
    T extends keyof AccountNewLegalOwner,
    F extends keyof AccountNewLegalOwner[T],
>(
    state: State,
    type: T,
    field: F,
) => _get(getTransferNewLegalOwner(state), [type, field], "");
export const getTransferNewIndividualLegalOwnerFullName = (state: State) => {
    const firstName = getTransferNewLegalOwnerField(state, "individual", "firstName");
    const lastName = getTransferNewLegalOwnerField(state, "individual", "lastName");
    return `${firstName} ${lastName}`.trim();
};

export const getCanSendTransferOrder = (state: State) => getTransfer(state).canSendOrder || false;
export const getHasSentTransferOrder = (state: State) => getTransfer(state).hasSentOrder || false;
export const getHasSubmittedTransferOrder = (state: State) => getTransfer(state).hasSubmittedOrder || false;
export const getHasRejectedTransferOrder = (state: State) => getTransfer(state).hasRejectedOrder || false;
export const getSelectedOfferingCode = (state: State) => getTransfer(state).selectedOfferingCode || "";
export const getAdminNumber = (state: State) => getTransfer(state).adminNumber || "";
export const shouldShowPinVerification = (state: State) => getTransfer(state).showPinVerification || false;
export const getTransferDate = (state: State) => getTransfer(state).selectedTransferDate;
export const getSelectedPhoneNumber = (state: State) => getTransfer(state).selectedPhoneNumber || "";
export const getHasSubmittedPortinData = (state: State) => getTransfer(state).hasSubmittedPortinData || false;

// Existing agreements data
export const getTransferExistingSubscription = (state: State): MaybeEmpty<ExistingSubscription> =>
    getTransfer(state).existingSubscription || {};
export const getTransferExistingSwitchAgreement = (state: State) => getTransfer(state).existingSwitch;
export const getTransferExistingLeaseAgreements = (state: State) => getTransfer(state).existingLeases || [];
export const getTransferExistingServices = (state: State) => getTransfer(state).existingServices || [];
export const getTransferExistingServicesOnBAN = (state: State) =>
    getTransferExistingServices(state).filter(
        (service) => !service.type.includes("INSURANCE") || service.type.includes("_BAN"),
    );
export const getTransferExistingInsurance = (state: State) =>
    getTransferExistingServices(state).find(
        (service) => service.type.includes("INSURANCE") && !service.type.includes("_BAN"),
    );

export const getTransferAgreementsToKeep = (state: State): AgreementToKeep => getTransfer(state).agreementsToKeep || {};

export const isAllowedToKeepExistingSubscription = (state: State) =>
    !!getTransferExistingSubscription(state).isAllowedToKeepExistingSubscription;

// Existing legal verification
export const isExistingLegalOwnerVerified = (state: State) => !!getTransfer(state).existingLegalOwnerVerified;
export const getExistingVerifiedCompanyAdmin = (state: State) => getTransfer(state).verifiedCompanyAdmin || "";
export const hasExistingLegalOwnerGivenConsent = (state: State) => !!getTransfer(state).isConsentGiven;
